import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  Stack,
  Divider,
  FormHelperText,
  Chip,
  Checkbox,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import JoditEditor from "jodit-react";
import { useNavigate, useLocation } from "react-router-dom"; // Add useLocation
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../redux/hooks";
import Loader from "../../components/common/Loader";
import Notification from "../../components/common/Notification"; // Add this import
import { getAllOrganCategories } from "../../redux/slices/organCategorySlice";
import {
  createDonorReview,
  updateDonorReview,
} from "../../redux/slices/donorReviewSlice";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

// --- Constants ---
const MAX_IMAGE_SIZE = 1 * 1024 * 1024; // 1MB
const MIN_IMAGE_SIZE = 1 * 1024; // 1KB
const RECEIVER_OPTIONS = ["SOTTO", "NOTTO"];
const MAX_CERTIFICATE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_THANKYOU_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB

// --- Styled Components (same as your code) ---
const StyledContainer = styled(Container)(({ theme }) => ({
  width: "100%",
  backgroundColor: theme.palette.common.white,
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing(3),
  margin: theme.spacing(3),
}));
const StyledHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: "16px 24px",
  position: "sticky",
  top: 0,
  zIndex: 1100,
  backgroundColor: theme.palette.common.white,
  borderBottom: `1px solid ${theme.palette.divider}`,
}));
const StyledFormSection = styled(Box)(({ theme }) => ({
  padding: "32px",
  marginBottom: "24px",
  borderBottom: `1px solid ${theme.palette.divider}`,
  "&:last-child": {
    borderBottom: "none",
    marginBottom: 0,
  },
}));
const ImageDropZone = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 300,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  backgroundColor: theme.palette.background.paper,
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));
const Tag = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  margin: "4px",
  padding: "4px 12px",
  backgroundColor: theme.palette.primary.light,
  borderRadius: "16px",
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
    marginLeft: "8px",
    cursor: "pointer",
    "&:hover": {
      color: theme.palette.error.main,
    },
  },
}));
const TagsContainer = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  padding: "8px 0",
});
const CloseButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: 8,
  right: 8,
  backgroundColor: theme.palette.background.paper,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));
const UploadIconContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
});

// --- Utility Functions ---
function extractOrganIds(arr) {
  // Handles both {organ: "..."} and direct string
  if (!Array.isArray(arr)) return [];
  return arr.map((item) =>
    typeof item === "string"
      ? item
      : item.organ
      ? typeof item.organ === "string"
        ? item.organ
        : item.organ._id // fallback if organ is object
      : item._id
  );
}
function extractOrganCounts(arr) {
  if (!Array.isArray(arr)) return {};
  return arr.reduce((acc, item) => {
    if (typeof item === "object" && item.organ && item.count) {
      const organId =
        typeof item.organ === "string" ? item.organ : item.organ._id;
      acc[organId] = item.count;
    }
    return acc;
  }, {});
}

function formatDateForInput(dateStr) {
  if (!dateStr) return "";
  // Handles both Date and string
  const d = new Date(dateStr);
  if (isNaN(d)) return "";
  return d.toISOString().split("T")[0];
}

// --- Organ Selection Component ---
const OrganSelectionWithCounter = ({
  category,
  selected,
  onSelect,
  count,
  onCountChange,
}) => {
  const handleCountChange = (e, newCount) => {
    e.preventDefault();
    e.stopPropagation();
    onCountChange(newCount);
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
      }}
      onClick={(e) => {
        e.preventDefault();
        onSelect(!selected);
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
        <Checkbox
          checked={selected}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onSelect(e.target.checked)}
        />
        {category.image && (
          <Box
            component="img"
            src={category.image}
            alt={category.name}
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              objectFit: "cover",
              mr: 1,
            }}
          />
        )}
        <Typography>{category.name}</Typography>
      </Box>
      {selected && (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            size="small"
            onClick={(e) => handleCountChange(e, Math.max(1, count - 1))}
            disabled={count <= 1}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ minWidth: "30px", textAlign: "center" }}>
            {count}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => handleCountChange(e, Math.min(2, count + 1))}
            disabled={count >= 2}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

// --- Main Form ---
const DonorReviewForm = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editData = location.state?.editData; // Get editData from navigation state
  const dispatch = useDispatch();
  const editor = useRef(null);
  const { loading: organLoading, organCategory } = useAppSelector(
    (state) => state.organCategory
  );
  const { loading: donorLoading } = useAppSelector(
    (state) => state.donorReview
  );

  // Combine both loading states
  const loading = organLoading || donorLoading;

  // --- State ---
  const [formData, setFormData] = useState({
    image: null,
    donorName: editData?.donorName || "",
    slug: editData?.slug || "",
    organCategoryId: extractOrganIds(editData?.organCategoryId),
    status: editData?.status || "active",
    description: editData?.description || "",
    location: editData?.location || "",
    seoTitle: editData?.seoTitle || "",
    seoDescription: editData?.seoDescription || "",
    seoKeywords: editData?.seoKeywords || "",
    preview: editData?.image || null,
    certificate: null,
    certificatePreview: editData?.certificate || null,
    donationHospitalName: editData?.donationHospitalName || "",
    donationHospitalCity: editData?.donationHospitalCity || "",
    organReceiver: editData?.organReceiver || "",
    donationDate: formatDateForInput(editData?.donationDate),
    organCounts: extractOrganCounts(editData?.organCategoryId),
    youtubeLink: editData?.youtubeLink || "",
    thankYouImage: null,
    thankYouImagePreview: editData?.thankYouImage || null,
  });

  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    dispatch(getAllOrganCategories());
  }, [dispatch]);

  // Update the onClose/navigation logic
  const handleClose = () => {
    navigate("/donor-review"); // Navigate back to list
  };

  const handleImageChange = (event) => {
    const file = event.target?.files?.[0] || event;
    if (!file) return;

    setNotification(null);

    if (!(file instanceof Blob)) {
      setErrors((prev) => ({
        ...prev,
        image: "Invalid file format",
      }));
      return;
    }

    if (file.size < MIN_IMAGE_SIZE || file.size > MAX_IMAGE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        image: "Image size must be between 1KB and 1MB",
      }));
      return;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width !== 1080 || img.height !== 1080) {
        setErrors((prev) => ({
          ...prev,
          image: "Image dimensions must be exactly 1080px × 1080px",
        }));
        URL.revokeObjectURL(img.src);
        return;
      }

      setFormData((prev) => ({
        ...prev,
        image: file,
        preview: img.src,
      }));
      setErrors((prev) => ({ ...prev, image: "" }));
    };

    img.onerror = () => {
      setErrors((prev) => ({
        ...prev,
        image: "Invalid image file format",
      }));
      URL.revokeObjectURL(img.src);
    };
  };

  const handleClearImage = () => {
    setFormData((prev) => ({
      ...prev,
      image: null,
      preview: null,
    }));
  };

  const handleSEODescriptionChange = (e) => {
    const value = e.target.value;
    if (value.length <= 100) {
      setFormData((prev) => ({
        ...prev,
        seoDescription: value,
      }));
      setErrors((prev) => ({
        ...prev,
        seoDescription: "",
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        seoDescription: "Description cannot exceed 100 characters",
      }));
    }
  };

  const handleKeywordChange = (e) => {
    const value = e.target.value;

    if (value.includes(",")) {
      const keywords = value
        .split(",")
        .map((k) => k.trim())
        .filter((k) => k);

      if (keywords.length <= 15) {
        setFormData((prev) => ({
          ...prev,
          seoKeywords: keywords.join(","),
        }));
        setErrors((prev) => ({ ...prev, seoKeywords: "" }));
      } else {
        setErrors((prev) => ({
          ...prev,
          seoKeywords: "Cannot exceed 15 keywords",
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        seoKeywords: value,
      }));
    }
  };

  const handleCertificateChange = (event) => {
    const file = event.target?.files?.[0] || event;
    if (!file) return;

    setNotification(null);

    if (!(file instanceof Blob)) {
      setErrors((prev) => ({
        ...prev,
        certificate: "Invalid file format",
      }));
      return;
    }

    if (file.size > MAX_CERTIFICATE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        certificate: "Certificate size must be less than 10MB",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      certificate: file,
      certificatePreview: URL.createObjectURL(file),
    }));
    setErrors((prev) => ({ ...prev, certificate: "" }));
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const renderOrganSelect = () => (
    <FormControl fullWidth required error={Boolean(errors.organCategoryId)}>
      <InputLabel id="organ-category-label">Organ Categories</InputLabel>
      <Select
        labelId="organ-category-label"
        multiple
        value={formData.organCategoryId}
        label="Organ Categories"
        onChange={(e) => {
          const newSelected = e.target.value;
          const newCounts = { ...formData.organCounts };
          newSelected.forEach((organId) => {
            if (!newCounts[organId]) {
              newCounts[organId] = 1;
            }
          });
          Object.keys(newCounts).forEach((organId) => {
            if (!newSelected.includes(organId)) {
              delete newCounts[organId];
            }
          });
          setFormData((prev) => ({
            ...prev,
            organCategoryId: newSelected,
            organCounts: newCounts,
          }));
        }}
        renderValue={(selected) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {selected.map((value) => {
              const category = organCategory?.find((cat) => cat._id === value);
              const count = formData.organCounts[value] || 1;
              return category ? (
                <Chip
                  key={value}
                  label={`${category.name} (${count})`}
                  size="small"
                  sx={{
                    bgcolor: "primary.light",
                    color: "primary.dark",
                  }}
                />
              ) : null;
            })}
          </Box>
        )}
      >
        {organCategory?.map((category) => (
          <MenuItem
            key={category._id}
            value={category._id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <OrganSelectionWithCounter
              category={category}
              selected={formData.organCategoryId.includes(category._id)}
              onSelect={(selected) => {
                const newSelected = selected
                  ? [...formData.organCategoryId, category._id]
                  : formData.organCategoryId.filter(
                      (id) => id !== category._id
                    );
                setFormData((prev) => ({
                  ...prev,
                  organCategoryId: newSelected,
                }));
              }}
              count={formData.organCounts[category._id] || 1}
              onCountChange={(newCount) => {
                setFormData((prev) => ({
                  ...prev,
                  organCounts: {
                    ...prev.organCounts,
                    [category._id]: newCount,
                  },
                }));
              }}
            />
          </MenuItem>
        ))}
      </Select>
      <FormHelperText error={Boolean(errors.organCategoryId)}>
        {errors.organCategoryId ||
          "Select one or more organ categories (max 2 per category)"}
      </FormHelperText>
    </FormControl>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous notifications
    setNotification(null);

    try {
      const formDataObj = new FormData();

      // Append all form fields, skip preview fields
      Object.keys(formData).forEach((key) => {
        if (
          key === "preview" ||
          key === "certificatePreview" ||
          key === "pdfPreview" ||
          key === "thankYouImagePreview"
        ) {
          return;
        }
        if (key === "image" && formData[key] instanceof Blob) {
          formDataObj.append("image", formData[key]);
        } else if (key === "certificate" && formData[key] instanceof Blob) {
          formDataObj.append("certificate", formData[key]);
        } else if (key === "thankYouImage" && formData[key] instanceof Blob) {
          formDataObj.append("thankYouImage", formData[key]);
        } else if (key === "organCategoryId") {
          formData[key].forEach((id) => {
            formDataObj.append("organCategoryId[]", id);
          });
        } else if (key === "transplantStatus") {
          formDataObj.append("transplantStatus", JSON.stringify(formData[key]));
        } else if (key === "organCounts") {
          formDataObj.append("organCounts", JSON.stringify(formData[key]));
        } else if (formData[key] !== null && formData[key] !== undefined) {
          formDataObj.append(key, formData[key].toString().trim());
        }
      });

      // Dispatch create or update action
      const action = editData
        ? updateDonorReview({ id: editData._id, data: formDataObj })
        : createDonorReview(formDataObj);
      const result = await dispatch(action).unwrap();

      if (result) {
        // Show success notification
        setNotification({
          type: "success",
          message: `Donor review ${
            editData ? "updated" : "created"
          } successfully!`,
        });

        // Navigate after a short delay to show success message
        setTimeout(() => {
          navigate("/donor-review", { replace: true });
        }, 1500);
      }
    } catch (err) {
      console.error("Form submission error:", err);

      // Show complete backend error
      setNotification({
        type: "error",
        message: err || "Operation failed. Please try again.",
      });
    }
  };

  const handleThankYouImageChange = (event) => {
    const file = event.target?.files?.[0] || event;
    if (!file) return;

    setNotification(null);

    if (!(file instanceof Blob)) {
      setErrors((prev) => ({
        ...prev,
        thankYouImage: "Invalid file format",
      }));
      return;
    }

    if (file.size > MAX_THANKYOU_IMAGE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        thankYouImage: "Image size must be less than 5MB",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      thankYouImage: file,
      thankYouImagePreview: URL.createObjectURL(file),
    }));
    setErrors((prev) => ({ ...prev, thankYouImage: "" }));
  };

  return (
    <StyledContainer>
      <StyledHeader>
        <IconButton onClick={handleClose} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          {editData ? "Edit" : "Add New"} Donor Review
        </Typography>
      </StyledHeader>
      <Box sx={{ backgroundColor: "common.white" }}>
        <form onSubmit={handleSubmit}>
          {/* First Row: Image Upload + Basic Info */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              mb: 3,
              "& > *": { flex: "1 1 50%" },
            }}
          >
            {/* Left Side - Image Upload */}
            <StyledFormSection
              sx={{
                minWidth: "400px",
                maxWidth: "50%",
                height: "fit-content",
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Donor Image
              </Typography>
              <Box sx={{ position: "relative" }}>
                <ImageDropZone
                  onClick={() => document.getElementById("image-input").click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const file = e.dataTransfer.files[0];
                    if (file) handleImageChange(file);
                  }}
                >
                  {formData.preview ? (
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <img
                        src={formData.preview}
                        alt="Organ Preview"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          objectFit: "contain",
                        }}
                      />
                      <CloseButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearImage();
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </CloseButton>
                    </Box>
                  ) : (
                    <UploadIconContainer>
                      <CloudUploadIcon
                        sx={{ fontSize: 64, color: "primary.main" }}
                      />
                      <Typography
                        variant="body1"
                        color="textSecondary"
                        align="center"
                      >
                        Drag and drop your image here
                      </Typography>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        align="center"
                      >
                        or click to browse
                      </Typography>
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 1 }}
                      >
                        (Image must be 1080px × 1080px and up to 1MB)
                      </Typography>
                    </UploadIconContainer>
                  )}
                </ImageDropZone>
                <input
                  id="image-input"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e)}
                  style={{ display: "none" }}
                />
              </Box>
              {errors.image && (
                <FormHelperText error sx={{ ml: 1 }}>
                  {errors.image}
                </FormHelperText>
              )}
            </StyledFormSection>

            {/* Right Side - Basic Information */}
            <StyledFormSection
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                height: "fit-content",
              }}
            >
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>

              <TextField
                fullWidth
                label="Donor Name"
                variant="outlined"
                value={formData.donorName}
                onChange={(e) => {
                  const newDonorName = e.target.value;
                  const newSlug = generateSlug(newDonorName);
                  setFormData((prev) => ({
                    ...prev,
                    donorName: newDonorName,
                    slug: newSlug,
                  }));
                }}
                required
              />

              <TextField
                fullWidth
                label="Slug Title"
                variant="outlined"
                value={formData.slug}
                InputProps={{
                  readOnly: true,
                }}
                helperText="Automatically generated from Donor Name"
              />

              {renderOrganSelect()}

              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Donation Date"
                type="date"
                variant="outlined"
                value={formData.donationDate || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    donationDate: e.target.value,
                  }))
                }
                InputLabelProps={{
                  shrink: true,
                }}
                required
                inputProps={{
                  max: new Date().toISOString().split("T")[0],
                }}
              />
            </StyledFormSection>
          </Box>

          <StyledFormSection>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <TextField
                fullWidth
                label="Location"
                variant="outlined"
                value={formData.location}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, location: e.target.value }))
                }
                required
              />
            </Box>
          </StyledFormSection>

          <StyledFormSection>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
              Description
            </Typography>
            <Box
              sx={{
                width: "100%",
                "& .jodit-container": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
                "& .jodit-workplace": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
                "& .jodit-wysiwyg": {
                  width: "100% !important",
                  maxWidth: "100%",
                  padding: "16px",
                },
                "& .jodit-toolbar__box": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
                "& .jodit-status-bar": {
                  width: "100% !important",
                  maxWidth: "100%",
                },
              }}
            >
              <JoditEditor
                ref={editor}
                value={formData.description}
                onBlur={(newContent) => {
                  setFormData((prev) => ({
                    ...prev,
                    description: newContent,
                  }));
                }}
              />
            </Box>
          </StyledFormSection>

          <StyledFormSection>
            <Typography variant="h6" gutterBottom>
              Certificate & Thank You Message
            </Typography>

            {/* Single Row for Both Uploads */}
            <Box
              sx={{
                display: "flex",
                gap: 3,
                "& > *": { flex: "1 1 50%" }, // Equal width for both
              }}
            >
              {/* Left Side - Donor Certificate */}
              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Donor Certificate
                </Typography>
                <Box sx={{ position: "relative" }}>
                  <ImageDropZone
                    onClick={() =>
                      document.getElementById("certificate-input").click()
                    }
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const file = e.dataTransfer.files[0];
                      if (file) handleCertificateChange(file);
                    }}
                  >
                    {formData.certificatePreview ? (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <img
                          src={formData.certificatePreview}
                          alt="Certificate Preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                        <CloseButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData((prev) => ({
                              ...prev,
                              certificate: null,
                              certificatePreview: null,
                            }));
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </CloseButton>
                      </Box>
                    ) : (
                      <UploadIconContainer>
                        <CloudUploadIcon
                          sx={{ fontSize: 48, color: "primary.main" }}
                        />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          align="center"
                        >
                          Drag and drop certificate
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          align="center"
                        >
                          or click to browse
                        </Typography>
                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                          (Up to 10MB)
                        </Typography>
                      </UploadIconContainer>
                    )}
                  </ImageDropZone>
                  <input
                    id="certificate-input"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleCertificateChange}
                    style={{ display: "none" }}
                  />
                </Box>
                {errors.certificate && (
                  <FormHelperText error sx={{ ml: 1 }}>
                    {errors.certificate}
                  </FormHelperText>
                )}
              </Box>

              {/* Right Side - Thank You Message Image */}
              <Box>
                <Typography
                  variant="subtitle1"
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Thank You Message Image
                </Typography>
                <Box sx={{ position: "relative" }}>
                  <ImageDropZone
                    onClick={() =>
                      document.getElementById("thankyou-image-input").click()
                    }
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const file = e.dataTransfer.files[0];
                      if (file) handleThankYouImageChange(file);
                    }}
                  >
                    {formData.thankYouImagePreview ? (
                      <Box
                        sx={{
                          position: "relative",
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <img
                          src={formData.thankYouImagePreview}
                          alt="Thank You Message Preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                        <CloseButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setFormData((prev) => ({
                              ...prev,
                              thankYouImage: null,
                              thankYouImagePreview: null,
                            }));
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </CloseButton>
                      </Box>
                    ) : (
                      <UploadIconContainer>
                        <CloudUploadIcon
                          sx={{ fontSize: 48, color: "primary.main" }}
                        />
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          align="center"
                        >
                          Drag and drop thank you image
                        </Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          align="center"
                        >
                          or click to browse
                        </Typography>
                        <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                          (Up to 5MB)
                        </Typography>
                      </UploadIconContainer>
                    )}
                  </ImageDropZone>
                  <input
                    id="thankyou-image-input"
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleThankYouImageChange(e)}
                    style={{ display: "none" }}
                  />
                </Box>
                {errors.thankYouImage && (
                  <FormHelperText error sx={{ ml: 1 }}>
                    {errors.thankYouImage}
                  </FormHelperText>
                )}
              </Box>
            </Box>
          </StyledFormSection>

          <StyledFormSection>
            <Typography variant="h6" gutterBottom>
              Organ Donation Hospital Information
            </Typography>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Hospital Name"
                variant="outlined"
                value={formData.donationHospitalName}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    donationHospitalName: e.target.value,
                  }))
                }
                required
              />
              <TextField
                fullWidth
                label="Hospital City"
                variant="outlined"
                value={formData.donationHospitalCity}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    donationHospitalCity: e.target.value,
                  }))
                }
                required
              />
              <FormControl fullWidth>
                <InputLabel>Organ Receiver Organization</InputLabel>
                <Select
                  value={formData.organReceiver}
                  label="Organ Receiver Organization"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      organReceiver: e.target.value,
                    }))
                  }
                  required
                >
                  {RECEIVER_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </StyledFormSection>

          <StyledFormSection
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              height: "fit-content",
            }}
          >
            <Typography variant="h6" gutterBottom>
              YouTube Video Link
            </Typography>
            <TextField
              fullWidth
              label="YouTube Video Link"
              variant="outlined"
              value={formData.youtubeLink}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  youtubeLink: e.target.value,
                }))
              }
              placeholder="https://www.youtube.com/watch?v=..."
            />
          </StyledFormSection>

          <StyledFormSection>
            <Typography variant="h6" gutterBottom>
              SEO Settings
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <Box sx={{ display: "flex", gap: 3 }}>
              <TextField
                fullWidth
                label="SEO Title"
                variant="outlined"
                value={formData.seoTitle}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    seoTitle: e.target.value,
                  }))
                }
              />
              <TextField
                fullWidth
                label="SEO Description"
                variant="outlined"
                multiline
                rows={3}
                value={formData.seoDescription}
                onChange={handleSEODescriptionChange}
                error={Boolean(errors.seoDescription)}
                helperText={
                  errors.seoDescription ||
                  `${formData.seoDescription.length}/100 characters`
                }
              />
            </Box>
          </StyledFormSection>

          <StyledFormSection>
            <FormControl fullWidth>
              <TextField
                fullWidth
                label="SEO Keywords"
                variant="outlined"
                value={formData.seoKeywords}
                onChange={handleKeywordChange}
                error={Boolean(errors.seoKeywords)}
                helperText={
                  errors.seoKeywords ||
                  "Type keywords separated by commas. Example: keyword1,keyword2,keyword3 (max 15)"
                }
                placeholder="Type keywords separated by commas"
              />

              <TagsContainer>
                {formData.seoKeywords.split(",").map((keyword, index) =>
                  keyword.trim() ? (
                    <Tag key={index}>
                      <Typography variant="body2" component="span">
                        {keyword.trim()}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          const keywords = formData.seoKeywords
                            .split(",")
                            .filter((_, i) => i !== index)
                            .join(",");
                          setFormData((prev) => ({
                            ...prev,
                            seoKeywords: keywords,
                          }));
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tag>
                  ) : null
                )}
              </TagsContainer>
            </FormControl>
          </StyledFormSection>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={loading} // Disable during loading
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={loading} // Use combined loading state
              >
                {loading ? (editData ? "Updating..." : "Creating...") : (editData ? "Update" : "Create")}
              </Button>
            </Stack>
          </Box>
        </form>

        {/* Notification */}
        {notification && (
          <Notification
            type={notification.type}
            message={notification.message}
            onClose={() => setNotification(null)}
            autoClose={notification.type === "success"}
            duration={3000}
          />
        )}

        {/* Keep your Loader component */}
        {loading && <Loader />}
      </Box>
    </StyledContainer>
  );
};

export default DonorReviewForm;
